import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";

export default function CreateRecipe({ user, onSave, onClose }) {
  const [recipe, setRecipe] = useState({
    title: '',
    description: '',
    ingredients: [''],
    instructions: '',
    prep_time: '',
    servings: '',
    category: 'almuerzo',
    health_tags: [],
    author_name: user?.full_name || 'Anónimo'
  });

  const healthTags = [
    'diabetes', 'hipertension', 'celiaco', 'vegetariano', 
    'vegano', 'bajo_sodio', 'bajo_azucar'
  ];

  const addIngredient = () => {
    setRecipe(prev => ({ ...prev, ingredients: [...prev.ingredients, ''] }));
  };

  const updateIngredient = (index, value) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index] = value;
    setRecipe(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const removeIngredient = (index) => {
    setRecipe(prev => ({ 
      ...prev, 
      ingredients: prev.ingredients.filter((_, i) => i !== index) 
    }));
  };

  const toggleHealthTag = (tag) => {
    setRecipe(prev => ({
      ...prev,
      health_tags: prev.health_tags.includes(tag)
        ? prev.health_tags.filter(t => t !== tag)
        : [...prev.health_tags, tag]
    }));
  };

  const handleSave = () => {
    const validIngredients = recipe.ingredients.filter(i => i.trim());
    if (!recipe.title.trim() || validIngredients.length === 0 || !recipe.instructions.trim()) {
      return;
    }
    
    onSave({
      ...recipe,
      ingredients: validIngredients,
      prep_time: recipe.prep_time ? Number(recipe.prep_time) : undefined,
      servings: recipe.servings ? Number(recipe.servings) : undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Nueva Receta</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
          <Input
            placeholder="Título de la receta *"
            value={recipe.title}
            onChange={(e) => setRecipe(prev => ({ ...prev, title: e.target.value }))}
          />

          <Textarea
            placeholder="Descripción breve"
            value={recipe.description}
            onChange={(e) => setRecipe(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="Tiempo (min)"
              value={recipe.prep_time}
              onChange={(e) => setRecipe(prev => ({ ...prev, prep_time: e.target.value }))}
            />
            <Input
              type="number"
              placeholder="Porciones"
              value={recipe.servings}
              onChange={(e) => setRecipe(prev => ({ ...prev, servings: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Ingredientes *</label>
            {recipe.ingredients.map((ingredient, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input
                  placeholder={`Ingrediente ${index + 1}`}
                  value={ingredient}
                  onChange={(e) => updateIngredient(index, e.target.value)}
                />
                {recipe.ingredients.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeIngredient(index)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addIngredient} className="w-full">
              <Plus className="w-4 h-4 mr-1" />
              Agregar ingrediente
            </Button>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Preparación *</label>
            <Textarea
              placeholder="Describe los pasos para preparar la receta..."
              value={recipe.instructions}
              onChange={(e) => setRecipe(prev => ({ ...prev, instructions: e.target.value }))}
              rows={5}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Etiquetas de salud</label>
            <div className="flex flex-wrap gap-2">
              {healthTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleHealthTag(tag)}
                  className={`
                    px-3 py-1.5 rounded-full text-sm transition-all
                    ${recipe.health_tags.includes(tag)
                      ? 'bg-green-100 text-green-800 border-2 border-green-500'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={onClose} variant="outline" className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="flex-1 bg-green-600 hover:bg-green-700">
              Crear Receta
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}